import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { FormControl, MenuItem, Select } from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography as MuiTypography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type UnrecognizedSecurity = {
  ticker: string;
  targetWeight: number;
  rowIndex: number;
};

export type AvailableSecurity = {
  id: string;
  ticker: string;
  name: string;
};

export type SecuritiesReviewDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (replacements: Record<string, string>) => void;
  unrecognizedSecurities: UnrecognizedSecurity[];
  availableSecurities: AvailableSecurity[];
  loading?: boolean;
  title?: string;
  description?: string;
  submitLabel?: string;
};

const GRID_COLUMNS = '1fr 40px 2fr 120px';
const ARROW_ICON_SIZE = 20;

export const SecuritiesReviewDialog = ({
  open,
  onClose,
  onSubmit,
  unrecognizedSecurities,
  availableSecurities,
  loading = false,
  title,
  description,
  submitLabel,
}: SecuritiesReviewDialogProps) => {
  const { t } = useTranslation(['modelPortfolios', 'shared']);
  const [replacements, setReplacements] = useState<Record<string, string>>({});

  useEffect(() => {
    const initial: Record<string, string> = {};
    unrecognizedSecurities.forEach((sec) => {
      initial[sec.ticker] = '';
    });
    setReplacements(initial);
  }, [unrecognizedSecurities]);

  const handleReplacementChange = (ticker: string, replacementId: string) => {
    setReplacements((prev) => ({
      ...prev,
      [ticker]: replacementId,
    }));
  };

  const allSecuritiesReplaced = unrecognizedSecurities.every(
    (sec) => replacements[sec.ticker] && replacements[sec.ticker] !== '',
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      data-testid='securities-review-dialog'
    >
      <DialogTitle onClose={onClose}>
        {title || t('modelPortfolios:importCsv.modalTitle', 'Import CSV file')}
      </DialogTitle>
      <DialogContent>
        <Box display='flex' flexDirection='column' gap={2.5}>
          <MuiTypography variant='bodyMedium'>
            {description ||
              t(
                'modelPortfolios:importCsv.reviewDescription',
                'The following securities are not available. Replace with Cash or another security to continue.',
              )}
          </MuiTypography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: GRID_COLUMNS,
              alignItems: 'center',
              gap: 2,
              pb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <MuiTypography variant='labelSmall' fontWeight='medium'>
              Unrecognized
            </MuiTypography>
            <Box />
            <MuiTypography variant='labelSmall' fontWeight='medium'>
              Replacement
            </MuiTypography>
            <MuiTypography variant='labelSmall' fontWeight='medium'>
              Target Weight
            </MuiTypography>
          </Box>

          {unrecognizedSecurities.map((security) => (
            <Box
              key={security.ticker}
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID_COLUMNS,
                alignItems: 'center',
                gap: 2,
              }}
            >
              <MuiTypography variant='bodyMedium' fontWeight='medium'>
                {security.ticker}
              </MuiTypography>

              <Box display='flex' justifyContent='center'>
                <ArrowForwardIcon
                  sx={{ fontSize: ARROW_ICON_SIZE, color: 'text.secondary' }}
                />
              </Box>

              <FormControl
                fullWidth
                size='small'
                error={!replacements[security.ticker]}
              >
                <Select
                  value={replacements[security.ticker] || ''}
                  onChange={(e) =>
                    handleReplacementChange(security.ticker, e.target.value)
                  }
                  displayEmpty
                  error={!replacements[security.ticker]}
                >
                  <MenuItem value='' disabled>
                    Select
                  </MenuItem>
                  {availableSecurities.map((sec) => (
                    <MenuItem key={sec.id} value={sec.id}>
                      {sec.ticker} - {sec.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <MuiTypography variant='bodyMedium'>
                {security.targetWeight}%
              </MuiTypography>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogFooter>
        <Box display='flex' justifyContent='end' gap={1}>
          <Button
            onClick={onClose}
            variant='tonal'
            label={t('shared:cancel')}
            disabled={loading}
            dataTestId='securities-review-cancel-button'
          />
          <Button
            onClick={() => onSubmit(replacements)}
            label={
              submitLabel ||
              t('modelPortfolios:importCsv.import', 'Import file')
            }
            disabled={!allSecuritiesReplaced || loading}
            loading={loading}
            dataTestId='securities-review-submit-button'
          />
        </Box>
      </DialogFooter>
    </Dialog>
  );
};

export default SecuritiesReviewDialog;
